import {action, computed, makeObservable, observable} from "mobx";
import {
  IPersonTypeService,
  PersonType
} from "../../../services/personTypeService";

/**
 * The canonical person type names as returned by the User API. These are the
 * stable identifiers used by the SPA to look up ids by name.
 */
export const PersonTypeName = {
  Independent: "Independent",
  Serviced: "Serviced"
} as const;

/**
 * Fallback ids used when the person types have not yet been fetched from the
 * BFF, or when the fetch fails. These mirror the values hardcoded in
 * `PersonTypeController` on the User API backend and exist so that consumers
 * which run synchronously at app start (e.g. model constructors) still get a
 * usable identifier while the real list is being loaded.
 *
 * Kept in a single place (this file only) rather than duplicated across the
 * SPA. Remove once PersonTypes are fully backend-driven and the SPA can defer
 * work until the fetch completes.
 */
const FALLBACK_IDS: Readonly<Record<string, string>> = {
  [PersonTypeName.Independent]: "57636730074660864",
  [PersonTypeName.Serviced]: "57636812471762944"
};

export interface IPersonTypesDomainStore {
  personTypes: PersonType[];
  isLoaded: boolean;
  loadPersonTypes: () => Promise<void>;
  independentPersonTypeId: string;
  servicedPersonTypeId: string;
  getIdByName: (name: string) => string;
  getById: (id: string) => PersonType | undefined;
  isIndependent: (id: string) => boolean;
  isServiced: (id: string) => boolean;
}

export class PersonTypesDomainStore implements IPersonTypesDomainStore {
  private readonly personTypeService: IPersonTypeService;
  personTypes: PersonType[] = [];
  isLoaded = false;

  constructor(personTypeService: IPersonTypeService) {
    makeObservable(this, {
      personTypes: observable,
      isLoaded: observable,
      loadPersonTypes: action,
      independentPersonTypeId: computed,
      servicedPersonTypeId: computed
    });
    this.personTypeService = personTypeService;
  }

  loadPersonTypes = async () => {
    const data = await this.personTypeService.getPersonTypes();
    this.personTypes = data.map((d) => ({id: d.id, name: d.name}));
    this.isLoaded = true;
  };

  get independentPersonTypeId(): string {
    return this.getIdByName(PersonTypeName.Independent);
  }

  get servicedPersonTypeId(): string {
    return this.getIdByName(PersonTypeName.Serviced);
  }

  getIdByName = (name: string): string => {
    return (
      this.personTypes.find((p) => p.name === name)?.id ??
      FALLBACK_IDS[name] ??
      ""
    );
  };

  getById = (id: string): PersonType | undefined =>
    this.personTypes.find((p) => p.id === id);

  isIndependent = (id: string): boolean => id === this.independentPersonTypeId;

  isServiced = (id: string): boolean => id === this.servicedPersonTypeId;
}
